class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.isOpen = false;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.quickSearchTrendingProduct = this.querySelector('[data-quick-trendingProduct]');

    if (this.input && this.predictiveSearchResults) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');

    form?.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const selectedLink = this.querySelector('[aria-selected="true"] a');

    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
      this.setShowPredictive();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  getOptions() {
    return Array.from(
      this.querySelectorAll(
        '[data-predictive-search] [role="option"]:not([hidden])'
      )
    ).filter((option) => option.querySelector('a, button'));
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const options = this.getOptions();
    if (!options.length) return;

    const selectedElement = this.querySelector('[aria-selected="true"]');
    let nextIndex = direction === 'up' ? options.length - 1 : 0;

    if (selectedElement) {
      const selectedIndex = options.indexOf(selectedElement);
      nextIndex =
        direction === 'up'
          ? (selectedIndex - 1 + options.length) % options.length
          : (selectedIndex + 1) % options.length;
      selectedElement.setAttribute('aria-selected', 'false');
    }

    const activeElement = options[nextIndex];
    activeElement.setAttribute('aria-selected', 'true');

    const activeControl = activeElement.querySelector('a, button');
    if (activeControl?.id) {
      this.input.setAttribute('aria-activedescendant', activeControl.id);
    }

    this.setLiveRegionText(activeElement.textContent.trim());
  }

  selectOption() {
    const selectedControl = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    selectedControl?.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLocaleLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const productLimit = Math.min(
      10,
      Math.max(1, Number(this.dataset.productToShow || 6))
    );
    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('resources[type]', 'product,collection');
    params.set('resources[limit]', String(productLimit));
    params.set('section_id', 'predictive-search');

    fetch(`${window.routes.predictive_search_url}?${params.toString()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search failed: ${response.status}`);
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
        const section = parsedDocument.querySelector(
          '#shopify-section-predictive-search'
        );

        if (!section) {
          throw new Error('Predictive search section was not found.');
        }

        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        console.error(error);
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text') || '読み込み中';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    this.setLiveRegionResults();
    this.open();
    this.setShowPredictive();
    this.setOthers();

    document.dispatchEvent(
      new CustomEvent('flatoo:predictive-search-rendered', {
        detail: { searchTerm: this.getQuery(), root: this }
      })
    );
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const resultText = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    )?.textContent;

    if (resultText) this.setLiveRegionText(resultText.trim());
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.setHiddenPredictive();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    selected?.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }

  setShowPredictive() {
    this.predictiveSearchResults.classList.add('is-show');
    this.predictiveSearchResults.classList.remove('is-hidden');
    this.quickSearchTrendingProduct?.classList.remove('is-show');
    this.quickSearchTrendingProduct?.classList.add('is-hidden');
  }

  setHiddenPredictive() {
    this.predictiveSearchResults.classList.add('is-hidden');
    this.predictiveSearchResults.classList.remove('is-show');
    this.quickSearchTrendingProduct?.classList.add('is-show');
    this.quickSearchTrendingProduct?.classList.remove('is-hidden');
  }

  setOthers() {
    if (window.product_swatch_style === 'slider') {
      const productList = this.querySelectorAll('.product');

      productList.forEach((element) => {
        const product = window.jQuery ? window.jQuery(element) : null;
        const productSwatch = product?.find('.card-swatch--slider');

        if (productSwatch?.length > 0) {
          const swatchGrid = productSwatch.find('.swatch');

          if (swatchGrid.length > 0 && !swatchGrid.hasClass('slick-initialized')) {
            Shopify.ProductSwatchs.showSwatchSlider({
              slider: swatchGrid,
              onComplete: null
            });
          }
        }
      });
    }

    if (window.compare?.show) {
      Shopify.ProductCompare?.setLocalStorageProductForCompare?.();
    }

    if (window.wishlist?.show) {
      Shopify.ProductWishlist?.setLocalStorageProductForWishlist?.();
    }

    if (this.checkNeedToConvertCurrency()) {
      const currencyCode = document
        .getElementById('currencies')
        ?.querySelector('.active')
        ?.getAttribute('data-currency');

      if (currencyCode && window.Currency) {
        Currency.convertAll(
          window.shop_currency,
          currencyCode,
          'span.money',
          'money_format'
        );
      }
    }
  }

  checkNeedToConvertCurrency() {
    return (
      (window.show_multiple_currencies &&
        window.Currency &&
        Currency.currentCurrency !== window.shop_currency) ||
      window.show_auto_currency
    );
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
